<template>
	<div class="block-fields">
		<div v-for="row in rows" :key="row.field.key || row.field.text" class="block-fields__row">
			<div v-if="row.section" class="pp-section">{{ row.section }}</div>

			<template v-if="hasControl(row.field)">
				<label class="pp-label">{{ row.field.label }}</label>

				<v-text-field v-if="row.field.type === 'text'" :value="local[row.field.key]" dense outlined hide-details @change="set(row.field.key, $event)"></v-text-field>

				<v-textarea v-else-if="row.field.type === 'textarea'" :value="local[row.field.key]" dense outlined hide-details rows="3" row-height="20" auto-grow @change="set(row.field.key, $event)"></v-textarea>

				<v-text-field v-else-if="row.field.type === 'url'" :value="local[row.field.key]" placeholder="https://" dense outlined hide-details :prepend-inner-icon="row.field.icon" @change="set(row.field.key, $event)"></v-text-field>

				<v-text-field v-else-if="row.field.type === 'number'" :value="local[row.field.key]" :suffix="row.field.suffix || 'px'" type="number" dense outlined hide-details @change="set(row.field.key, $event)"></v-text-field>

				<v-select v-else-if="row.field.type === 'select'" :value="local[row.field.key]" :items="row.field.options" dense outlined hide-details @change="set(row.field.key, $event)"></v-select>

				<div v-else-if="row.field.type === 'align'">
					<v-btn-toggle :value="local[row.field.key]" dense mandatory @change="set(row.field.key, $event)">
						<v-btn small value="left"><v-icon small>mdi-format-align-left</v-icon></v-btn>
						<v-btn small value="center"><v-icon small>mdi-format-align-center</v-icon></v-btn>
						<v-btn small value="right"><v-icon small>mdi-format-align-right</v-icon></v-btn>
					</v-btn-toggle>
				</div>
			</template>

			<color-field v-else-if="row.field.type === 'color'" :value="local[row.field.key] || ''" :label="row.field.label" @input="set(row.field.key, $event)"></color-field>

			<image-picker v-else-if="row.field.type === 'image'" :value="local[row.field.key]" :label="row.field.label" :prompt="local.imagePrompt || ''" @input="onImage(row.field, $event)"></image-picker>

			<template v-else-if="row.field.type === 'list'">
				<div v-for="(item, i) in (local[row.field.key] || [])" :key="i" class="block-fields__item">
					<div class="d-flex align-center mb-2">
						<span class="block-fields__item-title">{{ row.field.itemLabel || 'Elemento' }} {{ i + 1 }}</span>
						<v-spacer></v-spacer>
						<v-btn icon small :disabled="(local[row.field.key] || []).length <= (row.field.min || 0)" @click="removeItem(row.field, i)">
							<v-icon small>mdi-delete-outline</v-icon>
						</v-btn>
					</div>
					<block-fields :fields="row.field.fields" :value="item" @input="setItem(row.field, i, $event)"></block-fields>
				</div>
				<v-btn small text color="primary" :disabled="!!row.field.max && (local[row.field.key] || []).length >= row.field.max" @click="addItem(row.field)">
					<v-icon small left>mdi-plus</v-icon>Aggiungi {{ (row.field.itemLabel || 'elemento').toLowerCase() }}
				</v-btn>
			</template>

			<div v-else-if="row.field.type === 'hint'" class="pp-hint">{{ row.field.text }}</div>
		</div>
	</div>
</template>

<script>
import ImagePicker from './ImagePicker'
import ColorField from './ColorField'

/**
 * Pannello generico guidato dallo schema `fields` del blocco (vedi emailBlocks.js).
 * Input:  fields (schema), value (settings correnti)
 * Fa:     tiene una copia locale, emette `input` con l'intero oggetto aggiornato a ogni modifica
 * Output: evento input(settings)
 */
export default {
	name: 'block-fields',

	components: { 'image-picker': ImagePicker, 'color-field': ColorField },

	props: {
		fields: { type: Array, required: true },
		value: { type: Object, default: function() { return {} } }
	},

	data() {
		return { local: {} }
	},

	computed: {
		// Intestazione di sezione solo quando cambia rispetto al campo precedente
		rows() {
			var out = []
			var last = null
			for (var i = 0; i < this.fields.length; i++) {
				var f = this.fields[i]
				out.push({ field: f, section: f.section && f.section !== last ? f.section : null })
				if (f.section) last = f.section
			}
			return out
		}
	},

	watch: {
		value: {
			handler(val) { this.local = Object.assign({}, val || {}) },
			immediate: true
		}
	},

	methods: {
		// Etichetta sopra + controllo Vuetify senza etichetta propria: quella fluttuante sul bordo
		// scende a 10px e nel pannello stretto si confonde col contorno
		hasControl(field) {
			return ['text', 'textarea', 'url', 'number', 'select', 'align'].indexOf(field.type) !== -1
		},

		set(key, val) {
			var next = Object.assign({}, this.local)
			next[key] = val
			this.local = next
			this.$emit('input', Object.assign({}, next))
		},

		setItem(field, index, item) {
			var arr = (this.local[field.key] || []).slice()
			arr[index] = item
			this.set(field.key, arr)
		},

		addItem(field) {
			var arr = (this.local[field.key] || []).slice()
			arr.push(field.newItem ? field.newItem() : {})
			this.set(field.key, arr)
		},

		removeItem(field, index) {
			var arr = (this.local[field.key] || []).slice()
			arr.splice(index, 1)
			this.set(field.key, arr)
		},

		// Scegliendo un'immagine la larghezza puo' solo SCENDERE, mai salire: sopra i pixel
		// reali si vedrebbe sfocata, ma un valore piu' piccolo e' una scelta di impaginazione
		// e allargarlo d'ufficio la cancellerebbe — le immagini generate arrivano tutte a
		// 1200px e riporterebbero ogni blocco a tutta larghezza
		onImage(field, url) {
			this.set(field.key, url)
			if (!field.widthKey || !url || url.indexOf('data:') === 0) return
			var self = this
			var img = new Image()
			img.onload = function() {
				if (!img.naturalWidth) return
				var cap = Math.min(img.naturalWidth, field.maxWidth || 600)
				var current = parseInt(self.local[field.widthKey], 10)
				if (!current || current > cap) self.set(field.widthKey, String(cap))
			}
			img.src = url
		}
	}
}
</script>

<style scoped>
/* Vuetify di default usa 28px: troppo arioso rispetto al resto del builder */
.v-textarea >>> textarea {
	line-height: 20px;
}

.block-fields__row {
	margin-bottom: 12px;
}

.block-fields__row:last-child {
	margin-bottom: 0;
}

.block-fields__item {
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	padding: 6px 10px 12px;
	margin-bottom: 8px;
	background: #fafafa;
}

.block-fields__item-title {
	font-size: 13px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
}
</style>
